import * as DialogPrimitive from '@radix-ui/react-dialog';
import { isAxiosError } from 'axios';
import { Briefcase, Search, X } from 'lucide-react';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { backendClient } from '@/lib/api-client';
import { Loading } from '../shared';

interface SelectedWorkspace {
  id: string;
  wsid: string;
  name: string;
  database: string;
  path: string;
  workspace_id: string;
  workspace_name: string;
}

interface IManageWorkspacePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkspaceSelected?: (workspace: SelectedWorkspace) => void;
  authData: {
    access_token: string;
    customer_id: string;
    user_id?: string;
  };
}

interface Workspace {
  id: string;
  name: string;
  database: string;
  description?: string;
  owner?: string;
}

export const IManageWorkspacePicker: FC<IManageWorkspacePickerProps> = ({
  isOpen,
  onClose,
  onWorkspaceSelected,
  authData,
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isPartial, setIsPartial] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const requestGenerationRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const loadWorkspacePage = useCallback(
    async ({
      cursor,
      append,
      query,
      generation,
    }: {
      cursor?: string;
      append: boolean;
      query: string;
      generation: number;
    }) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const response = await backendClient.get('/imanage/workspaces', {
          params: {
            customer_id: authData.customer_id,
            user_id: authData.user_id,
            cursor,
            limit: 100,
            q: query || undefined,
          },
          headers: {
            'X-IManage-Access-Token': authData.access_token,
          },
        });

        if (generation !== requestGenerationRef.current) {
          return;
        }

        const workspaceList: Workspace[] = response.data.workspaces || [];
        setWorkspaces((current) => {
          if (!append) {
            return workspaceList;
          }
          const existingIds = new Set(current.map((workspace) => workspace.id));
          return [...current, ...workspaceList.filter((workspace) => !existingIds.has(workspace.id))];
        });
        setNextCursor(response.data.next_cursor || null);
        setIsPartial((current) => (
          append ? current || Boolean(response.data.partial) : Boolean(response.data.partial)
        ));
      } catch (err: unknown) {
        if (generation !== requestGenerationRef.current) {
          return;
        }
        console.error('Failed to load workspaces:', err);
        const detail = isAxiosError<{ detail?: string }>(err) ? err.response?.data?.detail : null;
        setError(detail || 'Failed to load workspaces');
      } finally {
        if (append) {
          loadMoreInFlightRef.current = false;
        }
        if (generation === requestGenerationRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [authData.access_token, authData.customer_id, authData.user_id]
  );

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    loadMoreInFlightRef.current = false;
    setWorkspaces([]);
    setNextCursor(null);
    setIsPartial(false);
    setError(null);

    if (isOpen && authData.access_token && authData.customer_id) {
      void loadWorkspacePage({
        append: false,
        query: debouncedSearchQuery,
        generation,
      });
    }
  }, [
    authData.access_token,
    authData.customer_id,
    debouncedSearchQuery,
    isOpen,
    loadWorkspacePage,
  ]);

  const loadMore = useCallback(() => {
    if (!nextCursor || isLoading || isLoadingMore || loadMoreInFlightRef.current) {
      return;
    }
    loadMoreInFlightRef.current = true;
    void loadWorkspacePage({
      cursor: nextCursor,
      append: true,
      query: debouncedSearchQuery,
      generation: requestGenerationRef.current,
    });
  }, [debouncedSearchQuery, isLoading, isLoadingMore, loadWorkspacePage, nextCursor]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !nextCursor || error) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px 0px',
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [error, loadMore, nextCursor]);

  const retryInitialLoad = useCallback(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    setWorkspaces([]);
    setNextCursor(null);
    setIsPartial(false);
    void loadWorkspacePage({
      append: false,
      query: debouncedSearchQuery,
      generation,
    });
  }, [debouncedSearchQuery, loadWorkspacePage]);

  const handleWorkspaceSelect = (workspace: Workspace) => {
    if (onWorkspaceSelected) {
      // Format workspace data for sync creation
      const workspaceData = {
        id: workspace.id,
        wsid: workspace.id.split('!')[1] || workspace.id,
        name: workspace.name,
        database: workspace.database,
        path: workspace.id,
        workspace_id: workspace.id,
        workspace_name: workspace.name,
      };

      console.log('Workspace selected:', workspaceData);
      onWorkspaceSelected(workspaceData);
    }
    onClose();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loading text='Loading workspaces' />
        </div>
      );
    }

    if (error && workspaces.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-sm text-red-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={retryInitialLoad}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (workspaces.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600">
              {isPartial
                ? 'Some workspaces could not be loaded'
                : searchQuery
                  ? 'No workspaces match your search'
                  : 'No workspaces found'}
            </p>
            {isPartial ? (
              <button
                type="button"
                onClick={retryInitialLoad}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
              >
                Try again
              </button>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {isPartial ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Some workspaces could not be loaded. The results shown may be incomplete.
          </div>
        ) : null}
        {workspaces.map((workspace) => (
          <button
            type="button"
            key={workspace.id}
            onClick={() => handleWorkspaceSelect(workspace)}
            className="w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          >
            <div className="flex items-start gap-3">
              <Briefcase className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{workspace.name}</div>
                {Boolean(workspace.description) && (
                  <div className="text-sm text-gray-500 truncate mt-0.5">
                    {workspace.description}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  Library: {workspace.database}
                </div>
              </div>
            </div>
          </button>
        ))}
        <div ref={loadMoreSentinelRef} className="min-h-1 py-2">
          {isLoadingMore ? <Loading text="Loading more workspaces" /> : null}
          {error ? (
            <div className="text-center">
              <p className="text-sm text-red-600 mb-2">{error}</p>
              <button
                type="button"
                onClick={loadMore}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Try loading more
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-100000 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-100001 w-[80vw] max-w-5xl max-h-[80vh] translate-x-[-50%] translate-y-[-50%] border border-stone-200 bg-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b">
            <DialogPrimitive.Title className="text-lg font-semibold mb-2">
              Select iManage Workspace
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-gray-500 mb-4">
              Choose a workspace to sync all its documents
            </DialogPrimitive.Description>

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search workspace names and descriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Content */}
          <div ref={scrollContainerRef} className="overflow-y-auto p-6 max-h-[500px]">
            {renderContent()}
          </div>

          {/* Close button */}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-xs p-1.5 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
